import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';

const steps = [
  'Answer eight quick questions',
  'Get your personalized report',
  'Review it with a specialist, free',
];

const trustTags = ['Free', 'Two minutes', 'No commitment'];

function ReportCoverIllustration() {
  return (
    <svg
      viewBox="0 0 210 297"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-full w-full"
      aria-hidden="true"
    >
      <rect width="210" height="297" rx="6" fill="#FFFFFF" />
      <rect x="0" y="0" width="210" height="297" rx="6" stroke="rgba(10, 186, 181, 0.08)" strokeWidth="1" />
      <rect x="18" y="28" width="96" height="10" rx="5" fill="#0abab5" />
      <rect x="18" y="52" width="174" height="6" rx="3" fill="#2a9d98" opacity="0.6" />
      <rect x="18" y="66" width="132" height="6" rx="3" fill="#2a9d98" opacity="0.4" />
      <rect x="18" y="96" width="72" height="6" rx="3" fill="#0abab5" />
      <rect x="18" y="112" width="174" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <rect x="18" y="122" width="156" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <rect x="18" y="132" width="168" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <rect x="18" y="142" width="96" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <rect x="18" y="166" width="72" height="6" rx="3" fill="#0abab5" />
      <rect x="18" y="182" width="174" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <rect x="18" y="192" width="156" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <rect x="18" y="202" width="168" height="4" rx="2" fill="#2a9d98" opacity="0.25" />
      <circle cx="165" cy="248" r="18" fill="#4dd4cf" />
      <path d="M158 248l5 5 10-10" stroke="#0abab5" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function QuizCTASection() {
  return (
    <section className="border-t border-rp-line bg-rp-bone py-14 md:py-20 lg:py-24">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="rp-container"
      >
        <div className="flex flex-col items-center gap-9 md:grid md:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] md:items-center md:gap-16 lg:gap-20">
          {/* Report preview */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.25 }}
            className="relative flex w-full items-center justify-center"
            aria-hidden="true"
          >
            <div className="relative w-[74vw] max-w-[300px]" style={{ aspectRatio: '210 / 297' }}>
              <div className="absolute inset-0 -rotate-3 overflow-hidden rounded-md shadow-[0_0_0_1px_rgba(10,186,181,0.06),-26px_38px_70px_-26px_rgba(10,186,181,0.5)]">
                <ReportCoverIllustration />
              </div>
            </div>
            <span className="absolute -bottom-2 -right-2 grid h-14 w-14 place-items-center rounded-full bg-rp-leaf-2 shadow-[0_0_0_6px_#f4f1ea,0_12px_24px_-8px_rgba(10,186,181,0.7)] md:bottom-1 md:right-2">
              <Check className="h-7 w-7 text-rp-paper" strokeWidth={2.6} />
            </span>
          </motion.div>

          {/* Copy */}
          <div className="w-full">
            <SectionHeader
              eyebrow="Free · personalized plan"
              title={
                <>
                  A free plan, <em className="mt-1 block font-medium not-italic text-rp-forest">built around you.</em>
                </>
              }
              description="Answer eight quick questions and we build a personalized peptide report from your goals, reviewed with you in a free 15-minute specialist consultation. No commitment."
              align="left"
            />

            <ol className="flex flex-col gap-3">
              {steps.map((step, index) => (
                <motion.li
                  key={step}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index, duration: 0.4 }}
                  className="flex items-center gap-3"
                >
                  <span className="w-5 flex-none font-mono text-[11px] font-medium text-rp-leaf-2">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[15px] text-rp-ink">{step}</span>
                </motion.li>
              ))}
            </ol>

            <div className="mt-7">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.2 }}>
                <Link to="/quiz" className="rp-btn-primary inline-flex">
                  Start the quiz
                </Link>
              </motion.div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-mono-mini font-bold uppercase tracking-[0.1em] text-rp-moss">
              {trustTags.map((tag, index) => (
                <span key={tag} className="inline-flex items-center gap-3">
                  {index > 0 && <span className="h-[5px] w-[5px] rounded-full bg-rp-leaf-2" />}
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
